/**
 * Animated version of the Muziekdocent drumpad logo.
 *
 * Pads light up in a repeating beat pattern (kick → hh → snare → hh →
 * kick → hh → clap → hh) synced to a CSS animation loop. Used on the
 * beta-gate page to give the waiting screen some life.
 *
 * Pure CSS animation — no JS timers, no React state. Each pad's `rect`
 * has a `--delay` custom property and the keyframes pulse its opacity
 * from the dim base to the neon fill and back.
 */

/**
 * The rhythm pattern: 8 steps in a 4/4 bar, each step is 250ms at
 * 120 BPM (one 8th note). Only the pads that fire on that step are
 * listed — everything else stays dim.
 *
 * Step  Beat   Pads
 * 0     1      kick (pink)
 * 1     1+     hh-closed (teal)
 * 2     2      snare (orange)
 * 3     2+     hh-closed (teal)
 * 4     3      kick (pink)
 * 5     3+     hh-closed (teal)
 * 6     4      clap (yellow)
 * 7     4+     hh-open (blue)
 */

const SIZE = 160
const CELLS = 4
const GAP = 5
const PAD = 14
const CELL = (SIZE - 2 * PAD - (CELLS - 1) * GAP) / CELLS // ~30
const R = 5 // corner radius

const DIM = '#2A2A2E'

// Layout: row, col → { fill, steps[] (which 8th-note steps this pad fires) }
type PadDef = { fill: string; steps: number[] }
const GRID: (PadDef | null)[][] = [
	// Row 0 (top)
	[null, null, { fill: '#22C55E', steps: [] }, null],
	// Row 1
	[null, { fill: '#14B8A6', steps: [1, 3, 5] }, null, { fill: '#06B6D4', steps: [7] }],
	// Row 2
	[{ fill: '#EAB308', steps: [6] }, null, null, { fill: '#F97316', steps: [2] }],
	// Row 3 (bottom)
	[{ fill: '#EC4899', steps: [0, 4] }, null, { fill: '#F97316', steps: [] }, null],
]

const TOTAL_STEPS = 8
const STEP_MS = 280 // slightly slower than true 120 BPM for visual readability
const LOOP_MS = TOTAL_STEPS * STEP_MS

function buildKeyframes(steps: number[]): string {
	if (steps.length === 0) return ''
	const parts: string[] = []
	for (const step of steps) {
		const onPct = ((step / TOTAL_STEPS) * 100).toFixed(1)
		const peakPct = (((step + 0.3) / TOTAL_STEPS) * 100).toFixed(1)
		const offPct = (((step + 0.8) / TOTAL_STEPS) * 100).toFixed(1)
		parts.push(`${onPct}% { opacity: 0.25 }`)
		parts.push(`${peakPct}% { opacity: 1 }`)
		parts.push(`${offPct}% { opacity: 0.25 }`)
	}
	return parts.join('\n')
}

let keyframeCounter = 0
function nextKeyframeName(): string {
	return `logo-beat-${keyframeCounter++}`
}

export function AnimatedLogo({
	size = SIZE,
	className,
}: {
	size?: number
	className?: string
}) {
	const scale = size / SIZE
	const rects: React.ReactNode[] = []
	const styleRules: string[] = []

	for (let row = 0; row < CELLS; row++) {
		for (let col = 0; col < CELLS; col++) {
			const x = PAD + col * (CELL + GAP)
			const y = PAD + row * (CELL + GAP)
			const def = GRID[row]?.[col]
			const fill = def?.fill ?? DIM
			const hasAnimation = def && def.steps.length > 0

			if (hasAnimation) {
				const name = nextKeyframeName()
				const kf = buildKeyframes(def.steps)
				styleRules.push(
					`@keyframes ${name} {\n  0%, 100% { opacity: 0.25 }\n  ${kf}\n}`,
				)
				rects.push(
					<rect
						key={`${row}-${col}`}
						x={x}
						y={y}
						width={CELL}
						height={CELL}
						rx={R}
						fill={fill}
						style={{
							opacity: 0.25,
							animation: `${name} ${LOOP_MS}ms ease-in-out infinite`,
						}}
					/>,
				)
			} else {
				rects.push(
					<rect
						key={`${row}-${col}`}
						x={x}
						y={y}
						width={CELL}
						height={CELL}
						rx={R}
						fill={fill}
						opacity={fill === DIM ? 1 : 0.7}
					/>,
				)
			}
		}
	}

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox={`0 0 ${SIZE} ${SIZE}`}
			fill="none"
			width={size}
			height={size}
			className={className}
			aria-hidden="true"
		>
			<style>{styleRules.join('\n')}</style>
			<rect width={SIZE} height={SIZE} rx={SIZE * 0.16} fill="#18181B" />
			{rects}
		</svg>
	)
}
